import {
  applyAuthorizationRoleOverride,
  buildDefaultAuthorization as buildSharedDefaultAuthorization,
  mergeAuthorizationPolicies,
} from '@skop-app/lib-domain/authorization'
import type { Authorization, Role } from '@skop-app/lib-domain/types'

import type { OrganizationMetadataEntry } from '@/utils/organizationMetadata'

import {
  getLegacyRestrictReferencesOverride,
  type PermissionDomainKey,
  type PermissionOperation,
  readTeamAuthorizationPolicyFromMetadata,
  type RolePermissionOverride,
  type TeamAuthorizationPolicyV1,
} from './policy'
import {
  type AuthorizationConstraints,
  EMPTY_AUTHORIZATION_CONSTRAINTS,
  resolveAuthorizationConstraints,
} from './templateProfiles'

type SessionAuthShape = Pick<
  Authorization,
  | 'user'
  | 'teams'
  | 'subscription'
  | 'canSendInternalMessage'
  | 'canInviteTeamMember'
  | 'canManageOrganization'
>

const DOMAIN_TO_AUTH_KEY = {
  definitions: 'definitionsAuth',
  properties: 'propertiesAuth',
  references: 'referencesAuth',
  options: 'optionsAuth',
  products: 'productsAuth',
  projects: 'projectsAuth',
  orders: 'ordersAuth',
  settings: 'settingsAuth',
  team: 'teamAuth',
} as const satisfies Record<PermissionDomainKey, keyof Authorization>

export const buildDefaultAuthorization = (
  sessionAuthorization: SessionAuthShape,
): Authorization => buildSharedDefaultAuthorization(sessionAuthorization)

export const buildPreviewAuthorizationForRole = (params: {
  role: Role
  override?: RolePermissionOverride
  template?: string | null
  isRotor?: boolean
}): Authorization => {
  const baseAuthorization = buildDefaultAuthorization({
    user: {
      id: `preview-${params.role.toLowerCase()}`,
      role: params.role,
      isMaster: false,
    },
    teams: [],
    subscription: false,
    canInviteTeamMember: params.role === 'ADMIN',
    canManageOrganization: params.role === 'ADMIN',
    canSendInternalMessage: false,
  })
  const constraints = resolveAuthorizationConstraints({
    role: params.role,
    template: params.template,
    isRotor: params.isRotor,
  })

  const withOverride =
    params.role === 'USER'
      ? applyAuthorizationRoleOverride(baseAuthorization, params.override)
      : baseAuthorization

  return applyAuthorizationConstraints(withOverride, constraints)
}

const applyAuthorizationConstraints = (
  authorization: Authorization,
  constraints: AuthorizationConstraints,
) => {
  let nextAuthorization = {
    ...authorization,
  }
  let hasAnyChanges = false

  if (constraints.booleans?.canInviteTeamMember) {
    nextAuthorization.canInviteTeamMember =
      constraints.booleans.canInviteTeamMember.lockedValue
    hasAnyChanges = true
  }

  if (constraints.booleans?.canManageOrganization) {
    nextAuthorization.canManageOrganization =
      constraints.booleans.canManageOrganization.lockedValue
    hasAnyChanges = true
  }

  const domainConstraints = constraints.domains
  if (!domainConstraints || Object.keys(domainConstraints).length === 0) {
    return hasAnyChanges ? nextAuthorization : authorization
  }

  for (const domain of Object.keys(
    domainConstraints,
  ) as PermissionDomainKey[]) {
    const authKey = DOMAIN_TO_AUTH_KEY[domain]
    const domainConstraint = domainConstraints[domain]
    if (!domainConstraint) continue

    let nextCrud = nextAuthorization[authKey]
    let hasChanges = false

    for (const operation of Object.keys(
      domainConstraint,
    ) as PermissionOperation[]) {
      const constraint = domainConstraint[operation]
      if (!constraint) continue

      nextCrud = {
        ...nextCrud,
        [operation]: constraint.lockedValue,
      }
      hasChanges = true
    }

    if (hasChanges) {
      nextAuthorization = {
        ...nextAuthorization,
        [authKey]: nextCrud,
      }
    }
  }

  return nextAuthorization
}

export type EffectiveAuthorizationResolution = {
  authorization: Authorization | undefined
  authorizationConstraints: AuthorizationConstraints
  parsedPolicy: {
    policy: TeamAuthorizationPolicyV1 | null
    source: 'none' | 'policy' | 'legacy'
    error: string | null
  }
  effectivePolicy: TeamAuthorizationPolicyV1 | null
}

export const resolveEffectiveAuthorization = (params: {
  sessionAuthorization?: Authorization
  metadata?: OrganizationMetadataEntry[]
  template?: string | null
  isRotor?: boolean
}): EffectiveAuthorizationResolution => {
  const sessionAuthorization = params.sessionAuthorization
  if (!sessionAuthorization?.user?.id) {
    return {
      authorization: sessionAuthorization,
      authorizationConstraints: EMPTY_AUTHORIZATION_CONSTRAINTS,
      parsedPolicy: {
        policy: null,
        source: 'none',
        error: null,
      },
      effectivePolicy: null,
    }
  }

  const baseAuthorization = buildDefaultAuthorization(sessionAuthorization)
  const metadata = params.metadata ?? []
  const parsedPolicy = readTeamAuthorizationPolicyFromMetadata(metadata)
  const legacyPolicy = getLegacyRestrictReferencesOverride({
    metadata,
    role: sessionAuthorization.user.role,
    hasPolicy: Boolean(parsedPolicy.policy),
  })
  const effectivePolicy = mergeAuthorizationPolicies(
    parsedPolicy.policy,
    legacyPolicy,
  )
  const authorizationConstraints = resolveAuthorizationConstraints({
    role: baseAuthorization.user.role,
    template: params.template,
    isRotor: params.isRotor,
  })
  const resolvedPolicySource =
    parsedPolicy.policy != null
      ? parsedPolicy.source
      : legacyPolicy
        ? 'legacy'
        : 'none'

  let authorization = baseAuthorization
  if (baseAuthorization.user.role === 'USER') {
    authorization = applyAuthorizationRoleOverride(
      authorization,
      effectivePolicy?.roles?.[baseAuthorization.user.role],
    )
  }

  authorization = applyAuthorizationConstraints(
    authorization,
    authorizationConstraints,
  )

  return {
    authorization,
    authorizationConstraints,
    parsedPolicy: {
      policy: parsedPolicy.policy,
      source: resolvedPolicySource,
      error: parsedPolicy.error,
    },
    effectivePolicy,
  }
}
